import React, {
	useState, useRef, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState } from '../../stores/types/types';
import { GraphicInterface } from '../../stores/reducers/graphics';
import { HIDE_GRAPHIC_MODAL, editGraphicsAction, updateGraphicsAction } from '../../stores/actions/graphics';
import { isVideo } from '../../utils/utils';
import ButtonClose from '../general-components/button-close';
import { checkFileName } from '../../utils/media';
import MediaVideo from '../general-components/media-video';
import { isPlayerSupported } from 'amazon-ivs-player';
import { useMachine } from '@xstate/react';
import { videoMachine, actions, videoActionCommands } from '../../utils/video-machine';

const {
	LOADED, FAIL, TIMING, TIMECHANGE, PLAY, END,
} = videoActionCommands;

interface OwnProps {
	graphicForGraphicModal: GraphicInterface;
}

export default function CustomGraphicsModal(props: OwnProps) {
	const { graphicForGraphicModal } = props;

	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);

	const [graphic, setGraphic] = useState<GraphicInterface>(graphicForGraphicModal);

	const dispatch = useDispatch();
	const graphicTitleInputRef = useRef<HTMLInputElement | null>(null);
	const videoEl = useRef<HTMLVideoElement | null>(null);
	const echoCancelledAudioRef = useRef<HTMLAudioElement>(null);
	const initialLoad = useRef<boolean>(true);
	const start = useRef<number | null>(null);

	const [current, send] = useMachine(videoMachine, {
		actions: { ...actions },
	});
	const {
		elapsed,
	} = current.context;

	function closeModal() {
		dispatch({
			type: HIDE_GRAPHIC_MODAL,
			payload: true,
		});
	}

	function updateGraphic(e: any) {
		if (graphic && checkFileName(e?.target.value)) {
			dispatch(editGraphicsAction(token!, {
				...graphic,
				title: e?.target
					? e.target.value
					: graphic.title,
			}, (editedGraphics: GraphicInterface) => {
				dispatch(updateGraphicsAction(editedGraphics));
			}));
		}
	}

	useEffect(() => {
		if (videoEl.current && Boolean(graphic.loops) && videoEl.current.paused) {
			videoEl.current.play();
		}
	}, [graphic]);

	useEffect(() => {
		setGraphic(graphicForGraphicModal);
		if (graphicTitleInputRef.current) {
			graphicTitleInputRef.current.value = graphicForGraphicModal.title;
		}
	}, [graphicForGraphicModal]);

	const isCreatorOrProducer = useMemo(
		() => (
			graphic.created_by === user?.gr_user
			|| graphic.created_by === user?.brandlive_profile
			|| host
		),
		[graphic, user, host],
	);

	return (
		<>
			<div className="custom-graphics-modal">
				<div className="custom-graphics-modal-top">
					<input
						ref={graphicTitleInputRef}
						type="text"
						onBlur={updateGraphic}
						className={classNames('name', !isCreatorOrProducer && 'not-creator')}
						defaultValue={graphic?.title ?? graphic?.original_name ?? ''}
						placeholder={isCreatorOrProducer ? 'Give me a name' : ''}
					/>

					<ButtonClose moreClassNames="close-modal-btn" onClick={() => closeModal()} />
				</div>

					<div className="custom-graphics-modal-center">
						{graphic
							&& (isVideo(graphic.images)
								? (
									<>
										<MediaVideo
											src={graphic.images}
											isHlsVideo={graphic.images.endsWith('.m3u8') && isPlayerSupported}
											onLoad={() => {}}
											ref={videoEl}
											loop={typeof graphic.loops === 'number' ? Boolean(graphic.loops) : false}
											muted={true}
											crossOrigin={'anonymous'}
											onLoadedData={() => {
												send(LOADED, {
													video: videoEl.current,
													audio: echoCancelledAudioRef.current,
													updatePresentedMedia: () => {},
													from: 'loading video',
												});

												send(PLAY, { doFireSync: false, from: 'autoplay' });
											}}
											onCanPlayThrough={() => {}}
											onPlay={() => {
												if (initialLoad.current) { start.current = window.performance.now(); }
											}}
											onTimeUpdate={() => {
												send(TIMING, { from: 'onTimeUpdate' });
												if (initialLoad.current && start.current) {
													initialLoad.current = false;
													const end = window.performance.now();
													const time = (end - start.current) / 1000;
													send(TIMECHANGE, { currentTime: time + elapsed });
												}
											}}
											onEnded={() => {
												send(END, { from: 'onEnded' });
											}}
											onError={() => {
												send(FAIL, { from: 'onError' });
											}}
											elapsed={elapsed}
										/>
										<audio
											ref={echoCancelledAudioRef}
										></audio>
									</>
								) : (
									<img
										src={graphic.images}
										alt={graphic.title ?? graphic.original_name}
									/>
								)
							)
						}
					</div>
			</div >
		</>
	);
}
